import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Save, Pencil, Info, Settings, FileCode, Copy, Edit3, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useToast } from '@/hooks/use-toast';
import { typographyPresetService } from '@/lib/typographyPresetService';
import {
  calculateTypeScale,
  scaleRatios,
  scaleSteps,
  popularFontFamilies,
  type TypeScale,
} from '@/lib/typographyScaleUtils';

interface TypographyEditorProps {
  presetId?: string;
  onBack: () => void;
  embedded?: boolean;
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  getCssRef?: React.MutableRefObject<(() => string) | null>;
  onChange?: () => void;
}

const FontConfigRow = ({ title, size, fontFamily, setFontFamily, fontWeight, setFontWeight, lineHeight, setLineHeight, letterSpacing, setLetterSpacing }: any) => (
  <Popover>
    <PopoverTrigger asChild>
      <div className="flex border border-border/50 rounded-xl overflow-hidden bg-background cursor-pointer hover:bg-accent/30 transition-colors group relative">
        <div className="w-24 shrink-0 flex items-center justify-center border-r border-border/50 bg-muted/20">
          <span 
            className="text-5xl text-primary" 
            style={{ fontFamily, fontWeight }}
          >
            Aa
          </span>
        </div>
        <div className="flex-1 p-5 flex flex-col justify-center min-w-0">
          <div className="text-[11px] font-semibold tracking-widest text-muted-foreground uppercase mb-1.5 truncate pr-6">{title}</div>
          <div className="text-xl font-medium text-foreground mb-1.5 truncate" style={{ fontFamily }}>{fontFamily}</div>
          <div className="text-sm text-muted-foreground truncate">{size} / w{fontWeight}</div>
        </div>
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <Settings className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </PopoverTrigger>
    <PopoverContent className="w-80 border-0" align="end">
      <div className="space-y-4">
        <div>
          <Label className="text-sm text-foreground mb-1.5 block">Font Family</Label>
          <Select value={fontFamily} onValueChange={setFontFamily}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {popularFontFamilies.map((font: any) => (
                <SelectItem key={font.name} value={font.name}>
                  <span style={{ fontFamily: font.name }}>{font.name}</span>
                  <span className="text-xs text-muted-foreground ml-2">({font.category})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-foreground mb-1.5 block">Weight</Label>
            <Select value={fontWeight} onValueChange={setFontWeight}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="300">300</SelectItem>
                <SelectItem value="400">400</SelectItem>
                <SelectItem value="500">500</SelectItem>
                <SelectItem value="600">600</SelectItem>
                <SelectItem value="700">700</SelectItem>
                <SelectItem value="800">800</SelectItem>
                <SelectItem value="900">900</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-foreground mb-1.5 block">Line-height</Label>
            <Input type="number" step="0.1" value={lineHeight} onChange={(e) => setLineHeight(Number(e.target.value))} className="h-9" />
          </div>
        </div>
        <div>
          <Label className="text-sm text-foreground mb-1.5 block">Letter-spacing</Label>
          <div className="relative">
            <Input type="number" step="0.01" value={letterSpacing} onChange={(e) => setLetterSpacing(Number(e.target.value))} className="h-9 pr-10" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">em</span>
          </div>
        </div>
      </div>
    </PopoverContent>
  </Popover>
);

export function TypographyEditor({ presetId, onBack, embedded = false, saveRef, getCssRef, onChange }: TypographyEditorProps) {
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('Untitled');
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [namingConvention, setNamingConvention] = useState('text');
  const [minFontSize, setMinFontSize] = useState(16);
  const [maxFontSize, setMaxFontSize] = useState(18);
  const [minScaleRatio, setMinScaleRatio] = useState(1.125);
  const [maxScaleRatio, setMaxScaleRatio] = useState(1.333);
  const [baseScaleIndex, setBaseScaleIndex] = useState('m');
  const [headingFontFamily, setHeadingFontFamily] = useState('Inter');
  const [bodyFontFamily, setBodyFontFamily] = useState('Inter');
  const [headingFontWeight, setHeadingFontWeight] = useState('500');
  const [headingLineHeight, setHeadingLineHeight] = useState(1.2);
  const [headingLetterSpacing, setHeadingLetterSpacing] = useState(0);
  const [bodyFontWeight, setBodyFontWeight] = useState('400');
  const [bodyLineHeight, setBodyLineHeight] = useState(1.6);
  const [bodyLetterSpacing, setBodyLetterSpacing] = useState(0);
  const [labelFontFamily, setLabelFontFamily] = useState('Inter');
  const [labelFontWeight, setLabelFontWeight] = useState('500');
  const [labelLineHeight, setLabelLineHeight] = useState(1.4);
  const [labelLetterSpacing, setLabelLetterSpacing] = useState(0.02);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');

  const [typeScale, setTypeScale] = useState<TypeScale[]>([]);

  const isInitialLoad = useRef(true);
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    onChange?.();
  }, [
    name, namingConvention, minFontSize, maxFontSize, minScaleRatio, maxScaleRatio,
    baseScaleIndex, headingFontFamily, bodyFontFamily, headingFontWeight, headingLineHeight,
    headingLetterSpacing, bodyFontWeight, bodyLineHeight, bodyLetterSpacing,
    labelFontFamily, labelFontWeight, labelLineHeight, labelLetterSpacing, typeScale, onChange
  ]);

  const generateClampValue = (minSize: number, maxSize: number): string => {
    const minViewport = 320;
    const maxViewport = 1200;
    const slope = (maxSize - minSize) / (maxViewport - minViewport);
    const yAxisIntersection = -minViewport * slope + minSize;
    return `clamp(${Math.round(minSize)}px, ${Math.round(yAxisIntersection)}px + ${(slope * 100).toFixed(2)}vw, ${Math.round(maxSize)}px)`;
  };

  const handleCopyClampValue = async (clampValue: string) => {
    try {
      await navigator.clipboard.writeText(clampValue);
      toast({
        title: 'Copied!',
        description: 'Clamp value copied to clipboard',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  const handleScaleValueChange = (scaleName: string, field: 'minSize' | 'maxSize', value: string) => {
    const numValue = Math.round(parseFloat(value));
    if (isNaN(numValue) || numValue <= 0) return;

    setTypeScale(prevScale =>
      prevScale.map(scale => {
        if (scale.name !== scaleName) return scale;

        // Validate that maxSize is always >= minSize
        if (field === 'minSize' && numValue > scale.maxSize) {
          toast({
            title: 'Invalid Value',
            description: 'Min size cannot be greater than max size',
            variant: 'destructive',
          });
          return scale;
        }
        if (field === 'maxSize' && numValue < scale.minSize) {
          toast({
            title: 'Invalid Value',
            description: 'Max size cannot be less than min size',
            variant: 'destructive',
          });
          return scale;
        }

        return { ...scale, [field]: numValue };
      })
    );
  };

  const handleVariableNameChange = (oldName: string, newName: string) => {
    // Remove the naming convention prefix if present
    const prefix = `${namingConvention}-`;
    const actualName = newName.startsWith(prefix) ? newName.slice(prefix.length) : newName;

    setTypeScale(prevScale =>
      prevScale.map(scale =>
        scale.name === oldName
          ? { ...scale, name: actualName }
          : scale
      )
    );
  };

  const handleClampValueChange = (scaleName: string, clampValue: string) => {
    // Parse clamp(minpx, calculation, maxpx)
    const clampMatch = clampValue.match(/clamp\(\s*([\d.]+)px\s*,\s*[^,]+,\s*([\d.]+)px\s*\)/);
    if (clampMatch) {
      const minSize = Math.round(parseFloat(clampMatch[1]));
      const maxSize = Math.round(parseFloat(clampMatch[2]));

      // Validate that maxSize is always >= minSize
      if (!isNaN(minSize) && !isNaN(maxSize) && minSize > 0 && maxSize > 0) {
        if (maxSize < minSize) {
          toast({
            title: 'Invalid Clamp Value',
            description: 'Max size in clamp() cannot be less than min size',
            variant: 'destructive',
          });
          return;
        }

        setTypeScale(prevScale =>
          prevScale.map(scale =>
            scale.name === scaleName
              ? { ...scale, minSize, maxSize }
              : scale
          )
        );
      }
    }
  };

  useEffect(() => {
    if (presetId) {
      loadPreset();
    }
  }, [presetId]);

  useEffect(() => {
    const scale = calculateTypeScale(
      minFontSize,
      maxFontSize,
      minScaleRatio,
      maxScaleRatio,
      baseScaleIndex
    );
    setTypeScale(scale);
  }, [minFontSize, maxFontSize, minScaleRatio, maxScaleRatio, baseScaleIndex]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const loadPreset = async () => {
    if (!presetId) return;

    try {
      setLoading(true);
      const preset = await typographyPresetService.getPresetById(presetId);
      if (preset) {
        setName(preset.name);
        setNamingConvention(preset.naming_convention);
        setMinFontSize(Math.round(Number(preset.min_font_size)));
        setMaxFontSize(Math.round(Number(preset.max_font_size)));
        setMinScaleRatio(Number(preset.min_scale_ratio));
        setMaxScaleRatio(Number(preset.max_scale_ratio));
        setBaseScaleIndex(preset.base_scale_index);
        setHeadingFontFamily(preset.heading_font_family);
        setBodyFontFamily(preset.body_font_family);
        setHeadingFontWeight(preset.heading_font_weight || '500');
        setHeadingLineHeight(Number(preset.heading_line_height) || 1.2);
        setHeadingLetterSpacing(Number(preset.heading_letter_spacing) || 0);
        setBodyFontWeight(preset.body_font_weight || '400');
        setBodyLineHeight(Number(preset.body_line_height) || 1.6);
        setBodyLetterSpacing(Number(preset.body_letter_spacing) || 0);
        setLabelFontFamily((preset as any).label_font_family || 'Inter');
        setLabelFontWeight((preset as any).label_font_weight || '500');
        setLabelLineHeight(Number((preset as any).label_line_height) || 1.4);
        setLabelLetterSpacing(Number((preset as any).label_letter_spacing) || 0.02);
      }
    } catch (error) {
      console.error('Error loading preset:', error);
      toast({
        title: 'Error',
        description: 'Failed to load preset',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    if (!name.trim()) {
      setName('Untitled');
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNameBlur();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  const handleExportCSS = () => {
    try {
      let css = ':root {\n';
      css += `  /* Typography Scale: ${name} */\n`;
      css += `  /* Generated with min size ${minFontSize}px, max size ${maxFontSize}px */\n`;
      css += `  /* Min ratio: ${minScaleRatio}, Max ratio: ${maxScaleRatio} */\n\n`;

      typeScale.forEach((scale) => {
        const clampValue = generateClampValue(scale.minSize, scale.maxSize);
        css += `  --${namingConvention}-${scale.name}: ${clampValue};\n`;
      });

      css += '\n  /* Font Families */\n';
      css += `  --font-heading: ${headingFontFamily}, sans-serif;\n`;
      css += `  --font-body: ${bodyFontFamily}, sans-serif;\n`;
      css += `  --font-label: ${labelFontFamily}, sans-serif;\n`;

      css += '\n  /* Font Weights */\n';
      css += `  --font-weight-heading: ${headingFontWeight};\n`;
      css += `  --font-weight-body: ${bodyFontWeight};\n`;
      css += `  --font-weight-label: ${labelFontWeight};\n`;

      css += '\n  /* Line Heights */\n';
      css += `  --line-height-heading: ${headingLineHeight};\n`;
      css += `  --line-height-body: ${bodyLineHeight};\n`;
      css += `  --line-height-label: ${labelLineHeight};\n`;

      css += '\n  /* Letter Spacing */\n';
      css += `  --letter-spacing-heading: ${headingLetterSpacing}em;\n`;
      css += `  --letter-spacing-body: ${bodyLetterSpacing}em;\n`;
      css += `  --letter-spacing-label: ${labelLetterSpacing}em;\n`;

      css += '}\n';

      const blob = new Blob([css], { type: 'text/css' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name.toLowerCase().replace(/\s+/g, '-')}-typography.css`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: 'Success',
        description: 'CSS file exported successfully',
      });
    } catch (error) {
      console.error('Error exporting CSS:', error);
      toast({
        title: 'Error',
        description: 'Failed to export CSS',
        variant: 'destructive',
      });
    }
  };

  const handleSave = async () => {
    if (!currentWorkspace) {
      console.error('No current workspace');
      toast({
        title: 'Error',
        description: 'No workspace selected. Please select a workspace first.',
        variant: 'destructive',
      });
      return;
    }

    if (!name.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a preset name',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);

      const presetData = {
        workspace_id: currentWorkspace.id,
        name,
        description: '',
        naming_convention: namingConvention,
        min_font_size: minFontSize,
        max_font_size: maxFontSize,
        min_scale_ratio: minScaleRatio,
        max_scale_ratio: maxScaleRatio,
        base_scale_index: baseScaleIndex,
        scale_mode: 'automatic' as const,
        heading_font_family: headingFontFamily,
        body_font_family: bodyFontFamily,
        heading_font_weight: headingFontWeight,
        heading_line_height: headingLineHeight,
        heading_letter_spacing: headingLetterSpacing,
        body_font_weight: bodyFontWeight,
        body_line_height: bodyLineHeight,
        body_letter_spacing: bodyLetterSpacing,
        label_font_family: labelFontFamily,
        label_font_weight: labelFontWeight,
        label_line_height: labelLineHeight,
        label_letter_spacing: labelLetterSpacing,
      };



      if (presetId) {
        await typographyPresetService.updatePreset(presetId, presetData);
        toast({
          title: 'Success',
          description: 'Typography preset updated successfully',
        });
      } else {
        const result = await typographyPresetService.createPreset(presetData);
        console.log('Created preset:', result);
        toast({
          title: 'Success',
          description: 'Typography preset created successfully',
        });
      }

      if (!embedded) onBack();
    } catch (error: any) {
      console.error('Error saving preset:', error);
      const errorMessage = error?.message || 'Failed to save preset';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Expose save function to parent via ref
  useEffect(() => {
    if (saveRef) {
      saveRef.current = handleSave;
    }
    return () => {
      if (saveRef) saveRef.current = null;
    };
  });

  // Expose CSS generator to parent via ref
  useEffect(() => {
    if (getCssRef) {
      getCssRef.current = () => {
        let css = `  /* Typography Scale: ${name} */\n`;
        css += `  /* Generated with min size ${minFontSize}px, max size ${maxFontSize}px */\n`;
        css += `  /* Min ratio: ${minScaleRatio}, Max ratio: ${maxScaleRatio} */\n\n`;

        typeScale.forEach((scale) => {
          const clampValue = generateClampValue(scale.minSize, scale.maxSize);
          css += `  --${namingConvention}-${scale.name}: ${clampValue};\n`;
        });

        css += '\n  /* Font Families */\n';
        css += `  --font-heading: ${headingFontFamily}, sans-serif;\n`;
        css += `  --font-body: ${bodyFontFamily}, sans-serif;\n`;
        css += `  --font-label: ${labelFontFamily}, sans-serif;\n`;

        css += '\n  /* Font Weights */\n';
        css += `  --font-weight-heading: ${headingFontWeight};\n`;
        css += `  --font-weight-body: ${bodyFontWeight};\n`;
        css += `  --font-weight-label: ${labelFontWeight};\n`;

        css += '\n  /* Line Heights */\n';
        css += `  --line-height-heading: ${headingLineHeight};\n`;
        css += `  --line-height-body: ${bodyLineHeight};\n`;
        css += `  --line-height-label: ${labelLineHeight};\n`;

        css += '\n  /* Letter Spacing */\n';
        css += `  --letter-spacing-heading: ${headingLetterSpacing}em;\n`;
        css += `  --letter-spacing-body: ${bodyLetterSpacing}em;\n`;
        css += `  --letter-spacing-label: ${labelLetterSpacing}em;\n`;

        return css;
      };
    }
    return () => {
      if (getCssRef) getCssRef.current = null;
    };
  });

  if (loading) {
    return null;
  }

  return (
    <div className={`${embedded ? 'flex-1' : 'h-screen'} flex flex-col overflow-hidden`}>
      <div className="flex-1 overflow-y-auto">
        <div className="w-full">
          <div className="p-6 space-y-6">
            {!embedded ? (
              <div className="flex items-center justify-between pb-6 border-b border-border/30">
                <div className="flex items-center gap-4">
                  <Button variant="ghost" size="icon" onClick={onBack} className="hover:bg-accent">
                    <ArrowLeft className="h-5 w-5 text-foreground" />
                  </Button>
                  <div className="flex items-center gap-2">
                    {isEditingName ? (
                      <Input
                        ref={nameInputRef}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={handleNameBlur}
                        onKeyDown={handleNameKeyDown}
                        className="text-xl font-semibold h-auto py-1 px-2"
                      />
                    ) : (
                      <div className="flex items-center gap-2 group">
                        <h2 className="text-xl font-semibold text-foreground">
                          {name}
                        </h2>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => setIsEditingName(true)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportCSS}
                    className="gap-2 border-border hover:bg-accent text-foreground"
                  >
                    <FileCode className="h-4 w-4 text-foreground" />
                    Export CSS
                  </Button>
                  <Button onClick={handleSave} disabled={saving} className="gap-2">
                    <Save className="h-4 w-4" />
                    {saving ? 'Saving...' : 'Save Preset'}
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="bg-card border border-border/50 rounded-xl overflow-hidden mb-8">
              <div className="flex items-center justify-between px-6 py-4 border-b border-border/50 bg-muted/20">
                <div className="flex items-center gap-3">
                  <span className="text-muted-foreground font-serif text-xl border-b border-muted-foreground leading-none pb-0.5">T</span>
                  <h3 className="text-[11px] font-semibold tracking-[0.2em] text-muted-foreground uppercase">Typography</h3>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 text-muted-foreground hover:text-foreground" 
                  onClick={() => setIsPreviewOpen(true)}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-6 flex flex-col gap-4">
                <FontConfigRow 
                  title="DISPLAY LG" 
                  size="64px"
                  fontFamily={headingFontFamily} 
                  setFontFamily={setHeadingFontFamily} 
                  fontWeight={headingFontWeight} 
                  setFontWeight={setHeadingFontWeight} 
                  lineHeight={headingLineHeight} 
                  setLineHeight={setHeadingLineHeight} 
                  letterSpacing={headingLetterSpacing} 
                  setLetterSpacing={setHeadingLetterSpacing} 
                />
                <FontConfigRow 
                  title="BODY MD" 
                  size="16px"
                  fontFamily={bodyFontFamily} 
                  setFontFamily={setBodyFontFamily} 
                  fontWeight={bodyFontWeight} 
                  setFontWeight={setBodyFontWeight} 
                  lineHeight={bodyLineHeight} 
                  setLineHeight={setBodyLineHeight} 
                  letterSpacing={bodyLetterSpacing} 
                  setLetterSpacing={setBodyLetterSpacing} 
                />
                <FontConfigRow 
                  title="LABEL MD" 
                  size="12px"
                  fontFamily={labelFontFamily} 
                  setFontFamily={setLabelFontFamily} 
                  fontWeight={labelFontWeight} 
                  setFontWeight={setLabelFontWeight} 
                  lineHeight={labelLineHeight} 
                  setLineHeight={setLabelLineHeight} 
                  letterSpacing={labelLetterSpacing} 
                  setLetterSpacing={setLabelLetterSpacing} 
                />
              </div>
            </div>

            <div className="flex flex-col gap-6">
              {/* Left Column - Configuration Fields */}
              <div className="space-y-4 bg-card border border-border/50 rounded-lg p-5 h-fit">
                {/* Naming Convention */}
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Label htmlFor="naming-convention" className="text-sm text-foreground">Naming Convention</Label>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <p className="text-xs">Used as prefix for CSS variables (e.g., --text-m)</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Input
                    id="naming-convention"
                    value={namingConvention}
                    onChange={(e) => setNamingConvention(e.target.value)}
                    placeholder="text"
                    className="h-9"
                  />
                </div>

                {/* Min Font Size */}
                <div>
                  <Label htmlFor="min-font-size" className="text-sm text-foreground mb-1.5 block">Min Font Size</Label>
                  <div className="relative">
                    <Input
                      id="min-font-size"
                      type="number"
                      value={minFontSize}
                      onChange={(e) => setMinFontSize(Math.round(Number(e.target.value)))}
                      className="pr-10 h-9"
                      step="1"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      px
                    </span>
                  </div>
                </div>

                {/* Max Font Size */}
                <div>
                  <Label htmlFor="max-font-size" className="text-sm text-foreground mb-1.5 block">Max Font Size</Label>
                  <div className="relative">
                    <Input
                      id="max-font-size"
                      type="number"
                      value={maxFontSize}
                      onChange={(e) => setMaxFontSize(Math.round(Number(e.target.value)))}
                      className="pr-10 h-9"
                      step="1"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      px
                    </span>
                  </div>
                </div>

                {/* Min Scale Ratio */}
                <div>
                  <Label htmlFor="min-scale-ratio" className="text-sm text-foreground mb-1.5 block">Min Scale Ratio</Label>
                  <Select
                    value={minScaleRatio.toString()}
                    onValueChange={(v) => setMinScaleRatio(Number(v))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {scaleRatios.map((ratio) => (
                        <SelectItem key={ratio.value} value={ratio.value.toString()}>
                          {ratio.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Max Scale Ratio */}
                <div>
                  <Label htmlFor="max-scale-ratio" className="text-sm text-foreground mb-1.5 block">Max Scale Ratio</Label>
                  <Select
                    value={maxScaleRatio.toString()}
                    onValueChange={(v) => setMaxScaleRatio(Number(v))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {scaleRatios.map((ratio) => (
                        <SelectItem key={ratio.value} value={ratio.value.toString()}>
                          {ratio.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Base Scale Index */}
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Label htmlFor="base-scale-index" className="text-sm text-foreground">Base Scale Index</Label>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <p className="text-xs">The scale index that matches your base font size</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Select value={baseScaleIndex} onValueChange={setBaseScaleIndex}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {scaleSteps.map((step) => (
                        <SelectItem key={step.key} value={step.key}>
                          {step.label} ({step.key})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

            </div>
            
            <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto border-0 focus:outline-none p-6" aria-describedby={undefined}>
                <div className="flex items-center justify-between mb-6">
                  <DialogTitle className="font-semibold text-foreground">Typography Scale</DialogTitle>
                  <div className="flex items-center bg-muted rounded-md p-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setViewMode('edit')}
                      className={`h-8 w-8 ${viewMode === 'edit' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setViewMode('preview')}
                      className={`h-8 w-8 ${viewMode === 'preview' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {viewMode === 'edit' ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-[180px_100px_100px_1fr_40px] gap-3 px-4 py-2 text-sm font-medium text-muted-foreground border-b border-border/30">
                      <div>Variable Name</div>
                      <div>Min Size</div>
                      <div>Max Size</div>
                      <div>Clamp Value</div>
                      <div></div>
                    </div>
                    {typeScale.map((scale) => {
                      const isBase = scale.name === baseScaleIndex;
                      const clampValue = generateClampValue(scale.minSize, scale.maxSize);
                      return (
                        <div
                          key={scale.name}
                          className={`grid grid-cols-[180px_100px_100px_1fr_40px] gap-3 px-4 py-3 rounded-lg transition-colors ${
                            isBase
                              ? 'bg-workspace-accent/10'
                              : 'hover:bg-muted/50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Input
                              value={`${namingConvention}-${scale.name}`}
                              onChange={(e) => handleVariableNameChange(scale.name, e.target.value)}
                              className="h-8 text-sm font-mono flex-1 border-0 bg-background/50 focus-visible:ring-1 focus-visible:ring-workspace-accent focus-visible:ring-offset-0"
                              placeholder="Variable name"
                            />
                            {isBase && (
                              <span className="text-xs font-medium text-white px-2 py-0.5 bg-workspace-accent rounded whitespace-nowrap">Base</span>
                            )}
                          </div>
                          <div>
                            <Input
                              type="number"
                              value={Math.round(scale.minSize)}
                              onChange={(e) => handleScaleValueChange(scale.name, 'minSize', e.target.value)}
                              className="h-8 text-sm border-0 bg-background/50 focus-visible:ring-1 focus-visible:ring-workspace-accent focus-visible:ring-offset-0"
                              step="1"
                              min="1"
                            />
                          </div>
                          <div>
                            <Input
                              type="number"
                              value={Math.round(scale.maxSize)}
                              onChange={(e) => handleScaleValueChange(scale.name, 'maxSize', e.target.value)}
                              className="h-8 text-sm border-0 bg-background/50 focus-visible:ring-1 focus-visible:ring-workspace-accent focus-visible:ring-offset-0"
                              step="1"
                              min="1"
                            />
                          </div>
                          <div>
                            <Input
                              value={clampValue}
                              onChange={(e) => handleClampValueChange(scale.name, e.target.value)}
                              className="h-8 text-xs font-mono border-0 bg-background/50 focus-visible:ring-1 focus-visible:ring-workspace-accent focus-visible:ring-offset-0"
                              placeholder="clamp(...)"
                            />
                          </div>
                          <div className="flex items-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleCopyClampValue(clampValue)}
                            >
                              <Copy className="h-4 w-4 text-foreground" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-6 py-4">
                    {typeScale.map((scale) => {
                      const fontFamily = scale.name.includes('xl') ? headingFontFamily : bodyFontFamily;
                      const fontWeight = scale.name.includes('xl') ? headingFontWeight : bodyFontWeight;
                      const lineHeight = scale.name.includes('xl') ? headingLineHeight : bodyLineHeight;
                      const letterSpacing = scale.name.includes('xl') ? headingLetterSpacing : bodyLetterSpacing;

                      return (
                        <div key={scale.name} className="flex items-center gap-6">
                          <code className="text-sm font-mono text-muted-foreground whitespace-nowrap w-24">
                            --{namingConvention}-{scale.name}
                          </code>
                          <p
                            className="text-foreground flex-1"
                            style={{
                              fontSize: `${scale.maxSize}px`,
                              fontFamily,
                              fontWeight,
                              lineHeight: lineHeight.toString(),
                              letterSpacing: `${letterSpacing}em`,
                            }}
                          >
                            The quick brown fox jumps over the lazy dog
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}
